
import { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import LandingHeader from './landing/LandingHeader';
import LandingForm from './landing/LandingForm';
import { cn } from '@/lib/utils';

interface AccessData {
  firstName: string;
  lastName: string;
  email: string;
  country: string;
  timestamp: string;
}

const ACCESS_SESSION_KEY = 'appSessionUnlocked';

const getValidAccessData = (): AccessData | null => {
  const accessData = localStorage.getItem('appAccess');

  if (!accessData) {
    return null;
  }

  try {
    const parsedData: AccessData = JSON.parse(accessData);

    const hasFirstName = parsedData.firstName?.trim() !== '';
    const hasLastName = parsedData.lastName?.trim() !== '';
    const hasEmail = parsedData.email?.trim() !== '';
    const hasCountry = parsedData.country?.trim() !== '';

    return hasFirstName && hasLastName && hasEmail && hasCountry ? parsedData : null;
  } catch {
    return null;
  }
};

const AccessCheck = () => {
  const [authorized, setAuthorized] = useState<boolean | null>(null);

  // DEV BYPASS: Skip login during development
  const DEV_BYPASS = false;

  useEffect(() => {
    if (DEV_BYPASS) {
      setAuthorized(true);
      return;
    }
    setAuthorized(!!getValidAccessData());
  }, []);

  const handleAccessGranted = () => {
    if (!getValidAccessData()) {
      setAuthorized(false);
      return;
    }
    setAuthorized(true);
  };

  // Show nothing while checking (prevents flash)
  if (authorized === null) return null;

  return (
    <div className="relative min-h-screen">
      <div className={cn(!authorized && 'pointer-events-none select-none opacity-30 blur-[2px]')}>
        <Outlet />
      </div>

      {!authorized && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md space-y-8 rounded-lg border bg-card p-8 shadow-lg">
            <LandingHeader />
            <LandingForm onAccessGranted={handleAccessGranted} />
          </div>
        </div>
      )}
    </div>
  );
};

export default AccessCheck;
